import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const COOKIE_KEY = 'apuestamente_cookie_consent';

type ConsentValue = 'accepted' | 'rejected' | null;

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [adsEnabled, setAdsEnabled] = useState(true);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);



  const dismiss = (value: ConsentValue) => {
    setLeaving(true);
    setTimeout(() => {
      localStorage.setItem(
        COOKIE_KEY,
        JSON.stringify({
          consent: value,
          analytics: value === 'accepted' ? analyticsEnabled : false,
          ads: value === 'accepted' ? adsEnabled : false,
          date: new Date().toISOString(),
        })
      );
      setVisible(false);
      setLeaving(false);
    }, 300);
  };

  const handleAcceptAll = () => {
    setAnalyticsEnabled(true);
    setAdsEnabled(true);
    dismiss('accepted');
  };

  const handleRejectAll = () => {
    dismiss('rejected');
  };

  const handleSavePreferences = () => {
    dismiss('accepted');
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-[200] transition-transform duration-300 ${
        leaving ? 'translate-y-full' : 'translate-y-0'
      }`}
    >
      <div className="bg-[#0d0f14] border-t border-[#2a2d3a] shadow-2xl">
        <div className="max-w-[1440px] mx-auto px-6 py-5">

          {!showDetails ? (
            /* ── Compact view ── */
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 flex items-center justify-center bg-[#00e87a]/10 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-shield-check-line text-[#00e87a] text-base"></i>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold mb-0.5">Usamos cookies</p>
                  <p className="text-[#6b7280] text-xs leading-relaxed">
                    Utilizamos cookies propias y de terceros para mejorar tu experiencia, analizar el tráfico y mostrarte publicidad relevante.
                    Puedes aceptarlas todas, rechazarlas o{' '}
                    <button
                      onClick={() => setShowDetails(true)}
                      className="text-[#00e87a] hover:underline cursor-pointer"
                    >
                      personalizar tu elección
                    </button>
                    . Más info en nuestra{' '}
                    <Link to="/cookies" className="text-[#00e87a] hover:underline">
                      Política de Cookies
                    </Link>.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0 w-full md:w-auto">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 md:flex-none px-5 py-2.5 border border-[#2a2d3a] text-[#a8b2c1] text-xs font-semibold rounded-lg hover:border-[#4a5568] hover:text-white transition-all cursor-pointer whitespace-nowrap"
                >
                  Rechazar
                </button>
                <button
                  onClick={() => setShowDetails(true)}
                  className="flex-1 md:flex-none px-5 py-2.5 border border-[#2a2d3a] text-[#a8b2c1] text-xs font-semibold rounded-lg hover:border-[#4a5568] hover:text-white transition-all cursor-pointer whitespace-nowrap"
                >
                  Personalizar
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 md:flex-none px-5 py-2.5 bg-[#00e87a] text-black text-xs font-bold rounded-lg hover:bg-[#00d070] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Aceptar todas
                </button>
              </div>
            </div>
          ) : (
            /* ── Detailed / preferences view ── */
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center bg-[#00e87a]/10 rounded-lg">
                    <i className="ri-settings-3-line text-[#00e87a] text-sm"></i>
                  </div>
                  <p className="text-white text-sm font-semibold">Preferencias de cookies</p>
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="w-7 h-7 flex items-center justify-center text-[#6b7280] hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-down-s-line text-lg"></i>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                {/* Necesarias */}
                <div className="bg-[#1a1d26] border border-[#2a2d3a] rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white text-xs font-semibold">Necesarias</p>
                    <div className="flex items-center gap-1.5">
                      <div className="w-8 h-4 bg-[#00e87a]/30 rounded-full flex items-center justify-end px-0.5">
                        <div className="w-3 h-3 bg-[#00e87a] rounded-full"></div>
                      </div>
                      <span className="text-[#00e87a] text-[10px] font-medium">Siempre</span>
                    </div>
                  </div>
                  <p className="text-[#6b7280] text-[11px] leading-relaxed">
                    Imprescindibles para el funcionamiento de la plataforma: sesión, autenticación y seguridad.
                  </p>
                </div>

                {/* Analíticas */}
                <div className="bg-[#1a1d26] border border-[#2a2d3a] rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white text-xs font-semibold">Analíticas</p>
                    <button
                      onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                      className={`w-8 h-4 rounded-full flex items-center transition-all cursor-pointer px-0.5 ${
                        analyticsEnabled ? 'bg-[#00e87a]/30 justify-end' : 'bg-[#2a2d3a] justify-start'
                      }`}
                    >
                      <div
                        className={`w-3 h-3 rounded-full transition-colors ${
                          analyticsEnabled ? 'bg-[#00e87a]' : 'bg-[#6b7280]'
                        }`}
                      ></div>
                    </button>
                  </div>
                  <p className="text-[#6b7280] text-[11px] leading-relaxed">
                    Nos ayudan a entender cómo usas la plataforma para mejorarla. Datos anonimizados (Google Analytics).
                  </p>
                </div>

                {/* Publicitarias */}
                <div className="bg-[#1a1d26] border border-[#2a2d3a] rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-white text-xs font-semibold">Publicitarias</p>
                    <button
                      onClick={() => setAdsEnabled(!adsEnabled)}
                      className={`w-8 h-4 rounded-full flex items-center transition-all cursor-pointer px-0.5 ${
                        adsEnabled ? 'bg-[#00e87a]/30 justify-end' : 'bg-[#2a2d3a] justify-start'
                      }`}
                    >
                      <div
                        className={`w-3 h-3 rounded-full transition-colors ${
                          adsEnabled ? 'bg-[#00e87a]' : 'bg-[#6b7280]'
                        }`}
                      ></div>
                    </button>
                  </div>
                  <p className="text-[#6b7280] text-[11px] leading-relaxed">
                    Permiten mostrar anuncios relevantes según tus intereses (Google AdSense).
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-end gap-3">
                <button
                  onClick={handleRejectAll}
                  className="w-full sm:w-auto px-5 py-2.5 border border-[#2a2d3a] text-[#a8b2c1] text-xs font-semibold rounded-lg hover:border-[#4a5568] hover:text-white transition-all cursor-pointer whitespace-nowrap"
                >
                  Rechazar todas
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="w-full sm:w-auto px-5 py-2.5 border border-[#00e87a] text-[#00e87a] text-xs font-semibold rounded-lg hover:bg-[#00e87a]/10 transition-all cursor-pointer whitespace-nowrap"
                >
                  Guardar preferencias
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="w-full sm:w-auto px-5 py-2.5 bg-[#00e87a] text-black text-xs font-bold rounded-lg hover:bg-[#00d070] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Aceptar todas
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
